// src/adminpanel/Sidebar.js
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./AdminPanel.css";
import { FaBars, FaTimes } from "react-icons/fa";
import { useAuth, ROLE_PERMISSIONS, ROLE_LABELS } from "../contexts/AuthContext";
import { useContentInfo, DEFAULT_CONTENT_INFO } from "../contexts/ContentContext";

// Nav items with their permission key
const NAV_ITEMS = [
  { path: "/admin", label: "Dashboard", permission: "dashboard" },
  { path: "/admin/orders", label: "Orders", permission: "orders" },
  { path: "/admin/AddProduct", label: "Add Product", permission: "addProduct" },
  { path: "/admin/ProductList", label: "Products List", permission: "productList" },
  { path: "/admin/StockManagement", label: "Stock Management", permission: "stockManagement" },
  { path: "/admin/ManageSale", label: "Manage Sale", permission: "manageSale" },
  { path: "/admin/ManageShipping", label: "Set Shipping Cost", permission: "manageShipping" },
  { path: "/admin/ManageBrands", label: "Manage Brands", permission: "manageBrands" },
  { path: "/admin/ContentManagement", label: "Content Management", permission: "contentManagement" },
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();

  const { contentInfo } = useContentInfo();
  const footerLogoURL = contentInfo?.footerLogoURL || DEFAULT_CONTENT_INFO.footerLogoURL;

  const role = user?.role || "superadmin";
  const allowedPerms = ROLE_PERMISSIONS[role] || [];
  const roleLabel = ROLE_LABELS[role] || "Admin";

  // Close sidebar when route changes (for mobile)
  useEffect(() => {
    if (window.innerWidth <= 768) {
      setIsOpen(false);
    }
  }, [location]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth <= 768 &&
        isOpen &&
        !event.target.closest('.admin-sidebar') &&
        !event.target.closest('.sidebar-toggle')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleLogout = () => {
    logout(navigate);
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Hamburger Menu Button */}
      <div className="sidebar-toggle" onClick={toggleSidebar}>
        {isOpen ? <FaTimes size={25} /> : <FaBars size={25} />}
      </div>

      {/* Overlay for mobile */}
      {isOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}

      {/* Sidebar */}
      <div className={`admin-sidebar ${isOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <Link to="/">
              <img src={footerLogoURL} alt="DawaCare Logo" style={{ maxHeight: "50px", objectFit: "contain" }} />
            </Link>
          </div>
          <p className="admin-badge"><b>Role:</b> {roleLabel}</p>
          {user?.name && (
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "12px", margin: "4px 0 0", textAlign: "start" }}>
              {user.name}
            </p>
          )}
        </div>

        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {NAV_ITEMS.filter((item) => allowedPerms.includes(item.permission)).map((item) => (
              <li
                key={item.path}
                className={location.pathname === item.path ? "active" : ""}
              >
                <Link to={item.path}>
                  <span className="menu-text">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
